import { Dices, ImagePlus, LoaderCircle, Pencil, X } from "lucide-react";
import { ChangeEvent, useId, useRef, useState, useSyncExternalStore } from "react";

import { IMAGE_ACCEPT } from "../lib/imageFile";
import { shuffledSample } from "../lib/random";
import { SketchCanvas } from "./SketchCanvas";
import "./AvatarPicker.css";

export const builtInAvatars = [
  { id: "builtin:aries", label: "Aries", src: "/avatars/aries.png" },
  { id: "builtin:taurus", label: "Taurus", src: "/avatars/taurus.png" },
  { id: "builtin:gemini", label: "Gemini", src: "/avatars/gemini.png" },
  { id: "builtin:cancer", label: "Cancer", src: "/avatars/cancer.png" },
  { id: "builtin:leo", label: "Leo", src: "/avatars/leo.png" },
  { id: "builtin:virgo", label: "Virgo", src: "/avatars/virgo.png" },
  { id: "builtin:libra", label: "Libra", src: "/avatars/libra.png" },
  { id: "builtin:scorpio", label: "Scorpio", src: "/avatars/scorpio.png" },
  { id: "builtin:sagittarius", label: "Sagittarius", src: "/avatars/sagittarius.png" },
  { id: "builtin:capricorn", label: "Capricorn", src: "/avatars/capricorn.png" },
  { id: "builtin:aquarius", label: "Aquarius", src: "/avatars/aquarius.png" },
  { id: "builtin:pisces", label: "Pisces", src: "/avatars/pisces.png" },
  // Animated mascots show their still frame when the viewer prefers reduced motion.
  { id: "builtin:mascot-fox", label: "Sketchy Fox", src: "/avatars/mascot-fox.gif", still: "/avatars/mascot-fox.png" },
  { id: "builtin:mascot-robot", label: "Beep Robot", src: "/avatars/mascot-robot.gif", still: "/avatars/mascot-robot.png" },
  { id: "builtin:mascot-bean", label: "Waving Bean", src: "/avatars/mascot-bean.gif", still: "/avatars/mascot-bean.png" },
  { id: "builtin:mascot-dice", label: "Lucky Dice", src: "/avatars/mascot-dice.gif", still: "/avatars/mascot-dice.png" },
  { id: "builtin:mascot-star", label: "Twinkle Star", src: "/avatars/mascot-star.gif", still: "/avatars/mascot-star.png" },
] as const;

const LEGACY_AVATARS: Record<string, (typeof builtInAvatars)[number]["id"]> = {
  "builtin:rocket": "builtin:aries",
  "builtin:fox": "builtin:scorpio",
  "builtin:plant": "builtin:virgo",
  "builtin:robot": "builtin:aquarius",
  "builtin:star": "builtin:leo",
  "builtin:cat": "builtin:cancer",
  "builtin:coffee": "builtin:taurus",
  "builtin:planet": "builtin:capricorn",
  "builtin:wave": "builtin:pisces",
  "builtin:party": "builtin:gemini",
};

export const defaultAvatar = builtInAvatars[0].id;

export function resolveBuiltInAvatar(avatar?: string) {
  const mapped = avatar ? LEGACY_AVATARS[avatar] ?? avatar : defaultAvatar;
  return builtInAvatars.find((item) => item.id === mapped) ?? (!avatar ? builtInAvatars[0] : undefined);
}

/** Picks a built-in avatar nobody in the room is using yet, falling back to any built-in once all are taken. */
export function randomBuiltInAvatar(taken: readonly string[] = [], random?: () => number): string {
  const used = new Set(taken.map((avatar) => resolveBuiltInAvatar(avatar)?.id ?? avatar));
  const free = builtInAvatars.filter((avatar) => !used.has(avatar.id));
  return shuffledSample([...(free.length ? free : builtInAvatars)], 1, random)[0].id;
}

const reducedMotionQuery = "(prefers-reduced-motion: reduce)";
function subscribeReducedMotion(onChange: () => void) {
  const media = typeof window === "undefined" ? undefined : window.matchMedia?.(reducedMotionQuery);
  media?.addEventListener?.("change", onChange);
  return () => media?.removeEventListener?.("change", onChange);
}
const prefersReducedMotion = () => typeof window !== "undefined" && Boolean(window.matchMedia?.(reducedMotionQuery).matches);

/** `premium` adds the animated premium frame; it is decoration only. */
export function AvatarArtwork({ avatar, label = "", className: extraClass = "", loading, premium = false }: { avatar?: string | null; label?: string; className?: string; loading?: "lazy" | "eager"; premium?: boolean }) {
  const className = premium ? `premium-avatar ${extraClass}`.trim() : extraClass;
  const reducedMotion = useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion, () => false);
  if (avatar === null) return <span className={`avatar-art avatar-pending ${className}`} role={label ? "img" : undefined} aria-label={label || undefined} aria-hidden={label ? undefined : true} />;
  const builtIn = resolveBuiltInAvatar(avatar);
  if (builtIn && (!avatar || avatar.startsWith("builtin:"))) {
    const src = reducedMotion && "still" in builtIn ? builtIn.still : builtIn.src;
    return <img className={`avatar-art avatar-builtin ${className}`} src={src} alt={label} loading={loading} role={label ? "img" : undefined} aria-label={label || undefined} aria-hidden={label ? undefined : true} />;
  }
  return <img className={`avatar-art avatar-upload ${className}`} src={avatar} alt={label} />;
}

type AvatarPickerProps = {
  value: string;
  googleAvatar?: string;
  previewUrl?: string;
  busy?: boolean;
  compact?: boolean;
  onSelect: (avatar: string) => void;
  onFile: (file: File) => void | Promise<void>;
};

export function AvatarPicker({ value, googleAvatar, previewUrl, busy = false, compact = false, onSelect, onFile }: AvatarPickerProps) {
  const inputId = useId();
  const drawingId = useId();
  const [showDrawing, setShowDrawing] = useState(false);
  const [drawingError, setDrawingError] = useState("");
  const [exporting, setExporting] = useState(false);
  const exportingLock = useRef(false);
  const disabled = busy || exporting;
  const customAvatar = previewUrl || (!value.startsWith("builtin:") && value !== googleAvatar ? value : "");

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file && !disabled) { setShowDrawing(false); setDrawingError(""); void onFile(file); }
    event.target.value = "";
  }

  function selectAvatar(avatar: string) {
    if (disabled) return;
    setShowDrawing(false);
    setDrawingError("");
    onSelect(avatar);
  }

  async function useDrawing(dataUrl: string) {
    if (busy || exportingLock.current) return;
    exportingLock.current = true;
    setExporting(true);
    setDrawingError("");
    try {
      const bytes = Uint8Array.from(atob(dataUrl.split(",")[1]), (character) => character.charCodeAt(0));
      await onFile(new File([bytes], "drawn-avatar.png", { type: "image/png" }));
      setShowDrawing(false);
    } catch {
      setDrawingError("We couldn’t use this drawing. Try again.");
    } finally {
      exportingLock.current = false;
      setExporting(false);
    }
  }

  return (
    <fieldset className={`avatar-picker ${compact ? "compact" : ""}`} disabled={disabled}>
      <legend>Choose your avatar</legend>
      <div className="avatar-options">
        {googleAvatar && <button type="button" className={value === googleAvatar && !previewUrl ? "selected" : ""} onClick={() => selectAvatar(googleAvatar)} aria-label="Use your Google profile photo" aria-pressed={value === googleAvatar && !previewUrl}><AvatarArtwork avatar={googleAvatar} /></button>}
        {builtInAvatars.map((avatar) => (
          <button
            type="button"
            key={avatar.id}
            className={value === avatar.id && !previewUrl ? "selected" : ""}
            onClick={() => selectAvatar(avatar.id)}
            aria-label={`Use ${avatar.label} avatar`}
            aria-pressed={value === avatar.id && !previewUrl}
          >
            <AvatarArtwork avatar={avatar.id} loading="lazy" />
          </button>
        ))}
        <label className={`avatar-upload-choice ${customAvatar ? "selected" : ""}`} htmlFor={inputId} aria-label="Upload your own avatar">
          <input id={inputId} type="file" accept={IMAGE_ACCEPT} onChange={chooseFile} disabled={disabled} />
          {disabled ? <LoaderCircle className="spin" size={20} /> : customAvatar ? <AvatarArtwork avatar={customAvatar} /> : <ImagePlus size={20} />}
        </label>
      </div>
      <button type="button" className="avatar-draw-toggle" aria-expanded={showDrawing} aria-controls={drawingId} disabled={disabled} onClick={() => { setShowDrawing((current) => !current); setDrawingError(""); }}>
        {showDrawing ? <X size={16} /> : <Pencil size={16} />}{showDrawing ? "Cancel drawing" : "Draw your avatar"}
      </button>
      {showDrawing && <div className="avatar-drawing-panel" id={drawingId}>
        <p>Make it yours with a quick sketch. Pick a colour and draw with your mouse or finger.</p>
        <SketchCanvas variant="avatar" disabled={disabled} onExport={(dataUrl) => { void useDrawing(dataUrl); }} />
        {drawingError && <div className="form-error" role="alert">{drawingError}</div>}
      </div>}
      {!compact && <small>{googleAvatar ? "Use your Google photo, choose an avatar, draw one, or upload a PNG, JPEG, WebP, or GIF." : "Pick a character, draw your own, or upload a PNG, JPEG, WebP, or GIF."} Images are compressed to 1 MB before upload. Large GIFs become still images.</small>}
    </fieldset>
  );
}

type AvatarChooserProps = Omit<AvatarPickerProps, "compact"> & {
  title?: string;
  hint?: string;
  /** Deals another random built-in avatar without opening the full picker. */
  onShuffle?: () => void;
};

/** Shows the current avatar with a floating picker for changing it. */
export function AvatarChooser({ title = "Your avatar", hint, onShuffle, ...picker }: AvatarChooserProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const current = picker.previewUrl || picker.value;
  const builtIn = current.startsWith("builtin:") ? resolveBuiltInAvatar(current) : undefined;

  function openPicker() {
    const dialog = dialogRef.current;
    if (!dialog || picker.busy) return;
    setOpen(true);
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
  }

  function closePicker() {
    const dialog = dialogRef.current;
    if (dialog?.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }
    setOpen(false);
  }

  return <div className="avatar-chooser">
    <button type="button" className="avatar-chooser-art" onClick={openPicker} disabled={picker.busy} aria-label="Change your avatar">
      <AvatarArtwork avatar={current} />
      <span className="avatar-chooser-badge" aria-hidden="true"><Pencil size={12} /></span>
    </button>
    <div className="avatar-chooser-copy">
      <strong>{title}</strong>
      <span>{hint ?? (builtIn ? builtIn.label : picker.googleAvatar && current === picker.googleAvatar ? "Your Google photo" : "Your own picture")}</span>
    </div>
    <div className="avatar-chooser-actions">
      {onShuffle && <button type="button" className="avatar-chooser-shuffle" onClick={onShuffle} disabled={picker.busy} aria-label="Deal another random avatar" title="Deal another random avatar"><Dices size={17} aria-hidden="true" /></button>}
      <button type="button" className="avatar-chooser-change" onClick={openPicker} disabled={picker.busy}>Change</button>
    </div>
    <dialog
      ref={dialogRef}
      className="avatar-dialog"
      aria-labelledby={titleId}
      onClose={() => setOpen(false)}
      onClick={(event) => { if (event.target === event.currentTarget) closePicker(); }}
    >
      <div className="avatar-dialog-panel">
        <header className="avatar-dialog-header">
          <AvatarArtwork avatar={current} className="avatar-dialog-current" />
          <div><h2 id={titleId}>Choose your avatar</h2><p>Tap a character, draw one, or upload a photo.</p></div>
          <button type="button" className="avatar-dialog-close" onClick={closePicker} aria-label="Close avatar picker"><X size={18} aria-hidden="true" /></button>
        </header>
        {/* Mount lazily so the animated avatars only download once someone opens the picker. */}
        {open && <AvatarPicker
          {...picker}
          onSelect={(avatar) => { picker.onSelect(avatar); closePicker(); }}
          onFile={async (file) => { await picker.onFile(file); closePicker(); }}
        />}
        <footer className="avatar-dialog-footer"><button type="button" onClick={closePicker}>Done</button></footer>
      </div>
    </dialog>
  </div>;
}
